import { useEffect, useState } from "react";
import api from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { PlusCircle, Upload, Trash2, Pencil } from "lucide-react";

const EMPTY = { name: "", designation: "", bio: "", photo_base64: "", type: "team", display_order: 0 };

export default function AdminTeamTab() {
  const [members, setMembers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/team");
      setMembers(data || []);
    } catch { toast.error("Could not load team"); }
  };

  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const startNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const startEdit = (m) => {
    setEditing(m.id);
    setForm({
      name: m.name || "",
      designation: m.designation || "",
      bio: m.bio || "",
      photo_base64: m.photo_base64 || "",
      type: m.type || "team",
      display_order: m.display_order || 0,
    });
    setOpen(true);
  };

  const readPhoto = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const r = new FileReader();
    r.onload = () => set("photo_base64", r.result);
    r.readAsDataURL(f);
    e.target.value = "";
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    if (!["founder", "team"].includes(form.type)) return toast.error("Choose a type");
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        designation: form.designation.trim(),
        bio: form.bio.trim(),
        photo_base64: form.photo_base64 || "",
        type: form.type,
        display_order: Number(form.display_order) || 0,
      };
      if (editing) {
        await api.patch(`/admin/team/${editing}`, payload);
        toast.success("Team member updated");
      } else {
        await api.post("/admin/team", payload);
        toast.success("Team member added");
      }
      setOpen(false); setForm(EMPTY); setEditing(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally { setBusy(false); }
  };

  const del = async (m) => {
    if (!window.confirm(`Remove ${m.name} from the team?`)) return;
    try {
      await api.delete(`/admin/team/${m.id}`);
      toast.success("Removed");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not delete"); }
  };

  const founders = members.filter((m) => m.type === "founder");
  const team = members.filter((m) => m.type !== "founder");

  const renderCard = (m) => (
    <div key={m.id} data-testid={`team-row-${m.id}`} className="p-4 bg-white border border-gray-200 rounded-2xl flex gap-4">
      {m.photo_base64 ? (
        <img src={m.photo_base64} alt={m.name} className="h-16 w-16 rounded-full object-cover" />
      ) : (
        <div className="h-16 w-16 rounded-full bg-[#FEF3E6] flex items-center justify-center font-display text-lg text-[#F48E2E]">
          {m.name.split(" ").map((n) => n[0]).join("").slice(0, 2)}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <div className="font-display text-lg truncate">{m.name}</div>
          <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${m.type === "founder" ? "bg-[#FEF3E6] text-[#F48E2E] border-[#F48E2E]/30" : "bg-gray-100 text-gray-600 border-gray-200"}`}>
            {m.type === "founder" ? "Founder" : "Team"}
          </span>
        </div>
        <div className="text-xs text-gray-500 mt-0.5">{m.designation || "—"} · Order {m.display_order || 0}</div>
        {m.bio && <div className="text-xs text-gray-600 mt-2 line-clamp-2">{m.bio}</div>}
      </div>
      <div className="flex flex-col gap-2">
        <Button data-testid={`team-edit-${m.id}`} onClick={() => startEdit(m)} variant="outline" className="rounded-full h-8 px-3"><Pencil className="h-3.5 w-3.5" /></Button>
        <Button data-testid={`team-del-${m.id}`} onClick={() => del(m)} variant="outline" className="rounded-full h-8 px-3 text-red-500 border-red-200 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5" /></Button>
      </div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="team-admin-tab">
      <div className="flex items-baseline justify-between">
        <div>
          <div className="font-display text-2xl">Team &amp; Founders</div>
          <p className="text-xs text-gray-500 mt-1">Manage the people shown on the About / Team page. Founders always render first, followed by team members. Use display order to fine-tune the sequence inside each bucket.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="team-add" onClick={startNew} className="rounded-full bg-[#F48E2E] hover:bg-[#D97815] text-white">
              <PlusCircle className="h-4 w-4 mr-2" /> Add member
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg bg-white max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editing ? "Edit team member" : "New team member"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                {form.photo_base64 ? (
                  <img src={form.photo_base64} alt="Preview" className="h-16 w-16 rounded-full object-cover" />
                ) : (
                  <div className="h-16 w-16 rounded-full bg-gray-100 flex items-center justify-center text-xs text-gray-400">Photo</div>
                )}
                <label className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 rounded-full border border-dashed border-[#F48E2E] text-[#F48E2E] text-xs hover:bg-[#FEF3E6]">
                  <Upload className="h-3.5 w-3.5" /> Upload photo
                  <input type="file" accept="image/*" hidden onChange={readPhoto} data-testid="team-photo-input" />
                </label>
                {form.photo_base64 && (
                  <Button type="button" variant="outline" className="rounded-full h-8 px-3 text-xs" onClick={() => set("photo_base64", "")}>Remove</Button>
                )}
              </div>
              <div>
                <Label>Name</Label>
                <Input data-testid="team-name" value={form.name} onChange={(e) => set("name", e.target.value)} />
              </div>
              <div>
                <Label>Designation</Label>
                <Input data-testid="team-designation" value={form.designation} onChange={(e) => set("designation", e.target.value)} placeholder="e.g. Co-Founder, Head of Curation" />
              </div>
              <div>
                <Label>Bio</Label>
                <Textarea data-testid="team-bio" rows={5} value={form.bio} onChange={(e) => set("bio", e.target.value)} placeholder="A short paragraph shown under the name on the About page." />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Type</Label>
                  <Select value={form.type} onValueChange={(v) => set("type", v)}>
                    <SelectTrigger data-testid="team-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="founder">Founder</SelectItem>
                      <SelectItem value="team">Team member</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Display order</Label>
                  <Input data-testid="team-order" type="number" value={form.display_order} onChange={(e) => set("display_order", e.target.value)} />
                </div>
              </div>
              <Button data-testid="team-save" onClick={save} disabled={busy} className="w-full rounded-full bg-[#F48E2E] hover:bg-[#D97815] text-white">
                {busy ? "Saving…" : editing ? "Save changes" : "Add member"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div>
        <div className="text-[11px] uppercase tracking-[0.2em] text-[#F48E2E] font-bold mb-2">Founders ({founders.length})</div>
        {founders.length === 0 ? (
          <div className="text-sm text-gray-500 py-6 text-center rounded-2xl bg-white border border-dashed border-gray-200">No founders added yet.</div>
        ) : (
          <div className="grid md:grid-cols-2 gap-3">{founders.map(renderCard)}</div>
        )}
      </div>

      <div>
        <div className="text-[11px] uppercase tracking-[0.2em] text-gray-500 font-bold mb-2">Team ({team.length})</div>
        {team.length === 0 ? (
          <div className="text-sm text-gray-500 py-6 text-center rounded-2xl bg-white border border-dashed border-gray-200">No team members added yet.</div>
        ) : (
          <div className="grid md:grid-cols-2 gap-3">{team.map(renderCard)}</div>
        )}
      </div>
    </div>
  );
}
